/**
 * Props-driven list body for the App workspace host.
 * Does not read useParams — the host supplies identity.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useObjectLabel } from '@/runtime/useObjectLabel'
import { useAppLocale } from '@/app/permissions/useAppLocale'
import { appNewRecordPath, appRecordPath } from '@/runtime/entityRoutes'
import Box from '@mui/material/Box'
import { RuntimeListView, resolveFormSchema, type ResolvedForm } from '@/runtime'
import { useAppWorkspaceStore } from './appWorkspaceStore'
import { resolveOpenMode } from './resolveOpenMode'
import { useMyPermissions } from '@/app/permissions/useMyPermissions'
import { canWriteRecord } from '@/app/permissions/permissionHelpers'
import { WorkspaceEmptyGate } from './WorkspaceEmptyGate'

export interface RuntimeListBodyProps {
  connectionId: string
  objectName: string
  formName?: string
  /** Workspace tab path — when omitted (thin page wrappers), skip openMode resolution. */
  tabPath?: string
  /** Reports the tab title — the object's synonym, not its configuration name. */
  onTitleChange?: (title: string) => void
}

export const RuntimeListBody: React.FC<RuntimeListBodyProps> = ({
  connectionId,
  objectName,
  formName: formNameProp,
  tabPath,
  onTitleChange,
}) => {
  const navigate = useNavigate()
  const { locale, defaultCode } = useAppLocale()
  // The tab strip must read like the sidebar: an object's synonym in the active
  // language, never the Designer name. Same registry+cache the ref fields use.
  const objectLabel = useObjectLabel(connectionId, objectName, locale ?? '', defaultCode)
  // Effective rights (Task 12, ERP Users & Access Rights epic) — fetched once
  // here so RuntimeListView itself stays hook-free (its ~20 existing suites
  // need no react-query/connection provider).
  const { perms } = useMyPermissions()

  useEffect(() => {
    if (objectLabel) onTitleChange?.(objectLabel)
  }, [objectLabel, onTitleChange])
  const [createFormName, setCreateFormName] = useState<string | undefined>(formNameProp)
  const [resolved, setResolved] = useState<ResolvedForm | null>(null)

  useEffect(() => {
    if (!connectionId || !objectName) return
    if (formNameProp) {
      setCreateFormName(formNameProp)
      return
    }
    let cancelled = false
    resolveFormSchema(connectionId, objectName, 'item').then((form) => {
      if (!cancelled) setCreateFormName(form?.schema?.name)
    })
    return () => {
      cancelled = true
    }
  }, [connectionId, objectName, formNameProp])

  useEffect(() => {
    if (!connectionId || !objectName) return
    let cancelled = false
    setResolved(null)
    const stampListPresentation = (mode: 'tab' | 'modal') => {
      if (!tabPath) return
      const current = useAppWorkspaceStore.getState().tabs.find((t) => t.path === tabPath)
      if (current?.presentation !== 'unresolved') return
      useAppWorkspaceStore.getState().setPresentation(tabPath, mode)
    }
    resolveFormSchema(connectionId, objectName, 'list')
      .then((form) => {
        if (cancelled) return
        setResolved(form ?? null)
        // List openMode toggle is deferred (autoplan A); default → tab.
        // WorkspaceRoute already opens lists as tab; this only heals hydrated leftovers.
        stampListPresentation(resolveOpenMode(form?.schema))
      })
      .catch(() => {
        if (cancelled) return
        stampListPresentation('tab')
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, objectName, tabPath])

  const handleOpen = useCallback(
    (recordId: string) => {
      navigate(
        createFormName
          ? `/app/${objectName}/${recordId}?form=${encodeURIComponent(createFormName)}`
          : appRecordPath(objectName, recordId),
      )
    },
    [navigate, objectName, createFormName],
  )

  const handleCreate = useCallback(
    (seed?: Record<string, unknown>) => {
      const path = createFormName
        ? `/app/${objectName}/new?form=${encodeURIComponent(createFormName)}`
        : appNewRecordPath(objectName)
      navigate(path, { state: seed ? { createSeed: seed } : undefined })
    },
    [navigate, objectName, createFormName],
  )

  if (!connectionId || !objectName) {
    return (
      <WorkspaceEmptyGate reason={!connectionId ? 'connection' : 'object'} />
    )
  }

  const passedSchema = resolved && resolved.source === 'custom' ? resolved.schema : null

  // canWriteRecord, not the raw per-op checks: describe_permissions pins
  // objects['Users'] = ['read'] even for a user admin, so a literal
  // create/delete check hid Create and row Delete from exactly the people
  // meant to manage members (whole-branch review I1). Same helper
  // RuntimeFormRenderer gates Save / Delete / Reset password on.
  const listPermissions = {
    canCreate: canWriteRecord(perms, objectName, 'create'),
    canDelete: canWriteRecord(perms, objectName, 'delete'),
  }

  return (
    <Box sx={{ height: '100%' }}>
      <RuntimeListView
        connectionId={connectionId}
        objectName={objectName}
        onOpen={handleOpen}
        onCreate={handleCreate}
        schema={passedSchema}
        degraded={resolved?.degraded}
        permissions={listPermissions}
        hideShellTitle
      />
    </Box>
  )
}
